import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  FileDownload,
  Dashboard,
  List as ListIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { usePartsCatalog } from './parts/usePartsCatalog';
import { usePartDocuments } from './parts/usePartDocuments';
import { filterParts, exportPartsToCSV, prepareEditFormData, getInitialFormData } from './parts/partsCatalogUtils';
import PartsCatalogFilters from './parts/PartsCatalogFilters';
import PartsCatalogTable from './parts/PartsCatalogTable';
import PartsCatalogDashboard from './parts/PartsCatalogDashboard';
import {
  QuickAddDialog,
  PartFormDialog,
  PartDocumentsDialog,
  DependencyDetailsDialog,
  SerialNumberDependenciesDialog
} from './parts/PartsCatalogDialogs';

/**
 * PartsCatalog Component
 * Main orchestrator component for the Parts Catalog feature
 * Manages state and coordinates between child components
 */
const API_URL = process.env.REACT_APP_API_URL || '/api';

const PartsCatalog = () => {
  const { user } = useAuth();
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [mainTab, setMainTab] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [formTab, setFormTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    manufacturer: '',
    partType: ''
  });
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Dependency dialog state
  const [dependencyDialogOpen, setDependencyDialogOpen] = useState(false);
  const [dependencyData, setDependencyData] = useState(null);
  const [partToDelete, setPartToDelete] = useState(null);

  // Serial number dependencies dialog (before removing a serial from part)
  const [serialDepsOpen, setSerialDepsOpen] = useState(false);
  const [serialDepsData, setSerialDepsData] = useState(null);
  const serialDepsOnConfirmRef = useRef(null);

  // Custom hooks for data operations
  const {
    parts,
    setParts,
    loading,
    pagination,
    dropdownOptions,
    fetchDropdownOptions,
    fetchParts,
    handleDelete: deletePart,
    handleForceDelete,
    handleSubmit: submitPart
  } = usePartsCatalog();

  // Custom hook for document management
  const {
    documentDialogOpen,
    setDocumentDialogOpen,
    selectedPartForDocuments,
    documentForm,
    setDocumentForm,
    documentUploading,
    documentUploadProgress,
    documentList,
    openDocumentDialog,
    handleDocumentUpload,
    handleDocumentDownload
  } = usePartDocuments();

  // Fetch dropdown options on mount
  useEffect(() => {
    fetchDropdownOptions();
  }, [fetchDropdownOptions]);

  // Reset to page 1 when filters, search, or pageSize change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, showArchived, searchTerm, pageSize]);

  // Fetch parts when page or any filter changes
  useEffect(() => {
    const controller = new AbortController();
    fetchParts(filters, showArchived, currentPage, pageSize, searchTerm, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, showArchived, searchTerm, pageSize]);

  // Use parts directly (search is now server-side)
  const filteredParts = parts;

  // Permission checks
  const canEdit = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER, ROLES.STORE_INSPECTOR].includes(user?.role);
  const canDelete = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER].includes(user?.role);

  // Handlers
  const handleQuickAdd = () => {
    setFormData(getInitialFormData());
    setSelectedPart(null);
    setFormTab(0);
    setQuickAddOpen(true);
  };

  const handleAdd = () => {
    setFormData(getInitialFormData());
    setSelectedPart(null);
    setFormTab(0);
    setOpen(true);
  };

  const handleEdit = (part) => {
    setSelectedPart(part);
    setFormData(prepareEditFormData(part));
    setFormTab(0);
    setOpen(true);
  };

  const handleDelete = async (partId, partNumber) => {
    const result = await deletePart(partId, partNumber);
    if (result.success) {
      // If we're on the last page and it becomes empty after deletion, go to previous page
      if (currentPage > 1 && parts.length === 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchParts(filters, showArchived, currentPage, pageSize, searchTerm);
      }
      alert('Part deleted successfully');
    } else if (result.hasDependencies) {
      // Part has dependencies - show dependency dialog
      setDependencyData(result.dependencyData);
      setPartToDelete(result.partToDelete);
      setDependencyDialogOpen(true);
    }
  };

  const handleSubmit = async (isQuickAdd = false) => {
    const result = await submitPart(formData, selectedPart, isQuickAdd);
    if (result.success) {
      if (isQuickAdd) {
        setQuickAddOpen(false);
      } else {
        setOpen(false);
      }
      // Refresh current page to show updated data
      await fetchParts(filters, showArchived, currentPage, pageSize, searchTerm);
      setSelectedPart(null);
      setFormData(getInitialFormData());
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleExportToCSV = exportPartsToCSV(filteredParts);

  const handleCloseDependencyDialog = () => {
    setDependencyDialogOpen(false);
    setDependencyData(null);
    setPartToDelete(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Parts Catalog</Typography>
        {mainTab === 1 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportToCSV}
              disabled={loading}
            >
              Export to CSV
            </Button>
            {canEdit && (
              <>
                <Button variant="outlined" startIcon={<Add />} onClick={handleQuickAdd}>
                  Quick Add
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
                  Add Part
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
      <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab icon={<Dashboard />} iconPosition="start" label="Dashboard" id="parts-tab-dashboard" aria-controls="parts-tabpanel-dashboard" />
        <Tab icon={<ListIcon />} iconPosition="start" label="Catalog" id="parts-tab-catalog" aria-controls="parts-tabpanel-catalog" />
      </Tabs>

      {mainTab === 0 && (
        <Box role="tabpanel" id="parts-tabpanel-dashboard" aria-labelledby="parts-tab-dashboard">
          <PartsCatalogDashboard />
        </Box>
      )}

      {mainTab === 1 && (
        <Box role="tabpanel" id="parts-tabpanel-catalog" aria-labelledby="parts-tab-catalog">
          {/* Filters */}
          <PartsCatalogFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        dropdownOptions={dropdownOptions}
      />

      {/* Result Count */}
      {(pagination.total > 0 || (!loading && parts.length > 0)) && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((Math.max(currentPage, 1) - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} part{pagination.total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Parts Table */}
      <PartsCatalogTable
        parts={filteredParts}
        loading={loading}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onManageDocuments={openDocumentDialog}
      />

      {/* Pagination Controls — keep visible while loading so page clicks are not lost after an aborted fetch */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                displayEmpty
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={pagination.totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            disabled={loading}
          />
        </Box>
      )}
        </Box>
      )}

      {/* Quick Add Dialog */}
      <QuickAddDialog
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      {/* Full Add/Edit Dialog */}
      <PartFormDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedPart(null);
          setFormData(getInitialFormData());
        }}
        selectedPart={selectedPart}
        formData={formData}
        setFormData={setFormData}
        formTab={formTab}
        setFormTab={setFormTab}
        dropdownOptions={dropdownOptions}
        displayTimezone={displayTimezone}
        onSubmit={handleSubmit}
        onDelete={selectedPart ? () => handleDelete(selectedPart._id, selectedPart.partNumber) : null}
        user={user}
        onRefreshDropdowns={fetchDropdownOptions}
        onOpenSerialDependencies={(data, onConfirm) => {
          setSerialDepsData(data);
          serialDepsOnConfirmRef.current = onConfirm;
          setSerialDepsOpen(true);
        }}
      />

      {/* Part Documents Dialog */}
      <PartDocumentsDialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        selectedPartForDocuments={selectedPartForDocuments}
        documentForm={documentForm}
        setDocumentForm={setDocumentForm}
        documentUploading={documentUploading}
        documentUploadProgress={documentUploadProgress}
        documentList={documentList}
        onUpload={handleDocumentUpload}
        onDownload={handleDocumentDownload}
      />

      {/* Dependency Details Dialog */}
      <DependencyDetailsDialog
        open={dependencyDialogOpen}
        onClose={handleCloseDependencyDialog}
        dependencyData={dependencyData}
        partToDelete={partToDelete}
      />

      {/* Serial number dependencies (confirm before removing serial from part) */}
      <SerialNumberDependenciesDialog
        open={serialDepsOpen}
        onClose={() => {
          setSerialDepsOpen(false);
          setSerialDepsData(null);
          serialDepsOnConfirmRef.current = null;
        }}
        serialNumber={serialDepsData?.serialNumber}
        partNumber={serialDepsData?.partNumber}
        dependencies={serialDepsData?.dependencies}
        onConfirm={() => {
          serialDepsOnConfirmRef.current?.();
          serialDepsOnConfirmRef.current = null;
          setSerialDepsOpen(false);
          setSerialDepsData(null);
        }}
      />
    </Box>
  );
};

export default PartsCatalog;
